import React, { useEffect, useReducer, useRef } from 'react';
import {
  Input,
  Button,
  Select,
  Card,
  Avatar,
  Layout,
  message,
  Upload
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import AppHeader from '../../../components/Dashboard/AppHeader';
import shadow from '../../../assets/pngs/shadow.png';
import axios from 'axios';
import userStore from '../../../zustand/store/userStore';
import Title from 'antd/es/typography/Title';
import { checkClassPromotionAPI } from '../../../zustand/API';

const { Option } = Select;
const { Content } = Layout;

const initialState = (user) => ({
  fullName: user?.fullName || '',
  password: '',
  parentEmail: user?.parentEmail || '',
  selectedClass: user?.grade || '',
  profileImage: null,
  previewUrl: user?.profilePic || '',
  canSelectClass: false,
});

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_PREVIEW_IMAGE':
      return { ...state, profileImage: action.file, previewUrl: action.url };
    case 'SET_CAN_SELECT_CLASS':
      return { ...state, canSelectClass: action.value };
    case 'RESET':
      return initialState(action.user);
    default:
      return state;
  }
};

const EditProfile = () => {
  const { user, updateUserProfile } = userStore();
  const [state, dispatch] = useReducer(reducer, user, initialState);
  const hasShownMessage = useRef(false);

  const { fullName, password, parentEmail, selectedClass, profileImage, previewUrl, canSelectClass } = state;
  const userId = user?._id;
  const email = user?.email;

  const checkIfCanSelectClass = async () => {
    const result = await checkClassPromotionAPI(email);

    dispatch({ type: 'SET_CAN_SELECT_CLASS', value: result.allowGradeSelect });

    if (!hasShownMessage.current && result.serverMessage) {
      result.success
        ? message.success(result.serverMessage)
        : message.error(result.serverMessage);
      hasShownMessage.current = true;
    }
  };


  useEffect(() => {
    if (user?.role === 'student') {
      checkIfCanSelectClass();
    }
  }, [user]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleImageChange = (info) => {
    const file = info.fileList[0]?.originFileObj;
    if (file) {
      const tempUrl = URL.createObjectURL(file);
      dispatch({ type: 'SET_PREVIEW_IMAGE', file, url: tempUrl });
      message.success('Image selected successfully');
    }
  };

  const handleSave = async () => {
    if (user?.role === 'student') {
      if (!parentEmail.trim()) {
        message.error('Parent email is required');
        return;
      }
      if (!selectedClass) {
        message.error('Class selection is required');
        return;
      }
    }
    try {
      const formData = new FormData();
      formData.append('fullName', fullName);
      formData.append('password', password);

      if (user?.role === 'student') {
        formData.append('parentEmail', parentEmail);
        formData.append('grade', selectedClass);
      }

      if (profileImage) {
        formData.append('profilePic', profileImage);
      }

      await updateUserProfile(formData, userId);
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Error updating profile');
    }
  };


  return (
    <Content className='p-2'>
      <AppHeader title="Edit Profile" content="Purpose: Allows the student to update their account details." />
      <div className="flex-1">
        <img src={shadow} alt="" className="w-full" />

        <Card className="mt-3 p-6 shadow-lg">
          <div className="flex flex-col sm:flex-row items-center sm:space-x-4">
            <Avatar
              size={64}
              src={previewUrl || undefined}
              style={{
                backgroundColor: !previewUrl ? '#1890ff' : undefined,
                color: '#fff',
                fontWeight: 'bold',
              }}
            >
              {!previewUrl && user?.fullName?.[0]?.toUpperCase()}
            </Avatar>

            <div className="mt-4 sm:mt-0">
              <h2 className="text-lg font-bold">{user?.fullName}</h2>
              <p className="text-gray-500">{user?.email}</p>

              <Upload
                key={previewUrl}
                beforeUpload={() => false}
                showUploadList={false}
                onChange={handleImageChange}
              >
                <Button icon={<UploadOutlined />} className="mt-2">
                  Change Profile Picture
                </Button>
              </Upload>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block mb-1 text-gray-700 font-medium">Full Name</label>
              <Input
                value={fullName}
                onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'fullName', value: e.target.value })}
              />
            </div>

            <div>
              <label className="block mb-1 text-gray-700 font-medium">Email</label>
              <Input value={email} disabled />
            </div>

            {user?.role === 'student' && (
              <>
                <div>
                  <label className="block mb-1 text-gray-700 font-medium">Parent Email</label>
                  <Input
                    value={parentEmail}
                    onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'parentEmail', value: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block mb-1 text-gray-700 font-medium">Class</label>
                  <Select
                    className="w-full"
                    value={selectedClass}
                    onChange={(value) => dispatch({ type: 'SET_FIELD', field: 'selectedClass', value })}
                    disabled={!canSelectClass}
                  >
                    {[9, 10, 11, 12].map(cls => (
                      <Option key={cls} value={cls}>{cls}</Option>
                    ))}
                  </Select>
                  {!canSelectClass && (
                    <p className="text-red-500 text-sm mt-1">
                      You are not eligible to change your class at this time.
                    </p>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="block mb-1 text-gray-700 font-medium">New Password</label>
              <Input.Password
                value={password}
                onChange={(e) => dispatch({ type: 'SET_FIELD', field: 'password', value: e.target.value })}
              />
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <Button type="primary" className="bg-green-500" onClick={handleSave}>
              Save Changes
            </Button>
            <Button onClick={() => dispatch({ type: 'RESET', user })}>Cancel</Button>
          </div>
        </Card>
      </div>
    </Content>
  );
};

export default EditProfile;
